import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api.js'
import { useSelectionStore } from '@/stores/selection.js'

export const useAuthStore = defineStore('auth', () => {
  const user    = ref(null)
  const loading = ref(false)

  const isLoggedIn = computed(() => user.value !== null)
  const isAdmin    = computed(() => ['admin', 'manager'].includes(user.value?.role))

  /**
   * Verifica se existe sessão activa via cookie.
   * Chamado no boot da app — não lança erro se não houver sessão.
   */
  async function fetchMe() {
    loading.value = true
    try {
      const { data } = await api.get('/auth/me')
      if (data.success) user.value = data.user
    } catch {
      user.value = null
    } finally {
      loading.value = false
    }
  }

  /**
   * POST /auth/login — o backend seta o cookie HttpOnly na resposta.
   */
  async function login(credentials) {
    const { data } = await api.post('/auth/login', credentials)
    if (data.success) user.value = data.user
    return data
  }

  /**
   * POST /auth/logout — o backend limpa o cookie.
   */
  async function logout() {
    try {
      await api.post('/auth/logout')
    } finally {
      user.value = null
    }
  }

  function hasPermission(perm) {
    if (!user.value) return false
    // Modelo da máquina do terminal seleccionado (null = genérico). Restringe também admin/manager.
    // Lê de user.terminals (vem fresco do /me) e não da cópia guardada na selecção.
    const terminalId = useSelectionStore().selectedTerminal?.id
    const modelPerms = (user.value.terminals ?? []).find(t => t.id === terminalId)?.model_permissions
    if (modelPerms && !modelPerms.includes(perm)) return false
    if (['admin', 'manager'].includes(user.value.role)) return true
    const perms = user.value.permissions_lists ?? user.value.permissions
    if (Array.isArray(perms)) return perms.includes(perm)
    return perms?.[perm] === true
  }

  function hasAnyPermission(...perms) {
    return perms.some(p => hasPermission(p))
  }

  function hasTerminal(terminalId) {
    if (!user.value) return false
    if (['admin', 'manager'].includes(user.value.role)) return true
    return (user.value.terminals ?? []).includes(terminalId)
  }

  return {
    user,
    loading,
    isLoggedIn,
    isAdmin,
    fetchMe,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    hasTerminal,
  }
})
